/**
 * @fileoverview Utility mapping an interaction site to its type-specific
 * agent digest route. Used so "Back to Agents" returns to the digest scoped
 * to the agent's type (e.g. a Canvas agent returns to the Canvas Step Agents
 * digest) rather than the generic all-agents digest.
 *
 * The route builders are held in a `Record<InteractionSite, ...>` so adding a
 * new interaction site constant to shared_constants.json fails compilation
 * until a route is provided for it.
 * @owner @Appboy/intelligence
 */

import type { InteractionSite } from "./types";
import { INTERACTION_SITES } from "./types";

/** Route to the type-specific agent digest for a given interaction site. */
export type AgentDigestRoute =
  | {
      pathname: "/agent_console/canvas_agents/:appGroupId";
      params: { appGroupId: string };
    }
  | {
      pathname: "/agent_console/data_agents/:appGroupId";
      params: { appGroupId: string };
    }
  | {
      pathname: "/agent_console/qa_agents/:appGroupId";
      params: { appGroupId: string };
    };

/**
 * Builds the digest route for each interaction site. Keyed by `InteractionSite`
 * so a missing entry is a compile-time error when a new site is added.
 */
const agentDigestRouteBuilders: Record<InteractionSite, (appGroupId: string) => AgentDigestRoute> = {
  [INTERACTION_SITES.CANVAS]: (appGroupId) => ({
    pathname: "/agent_console/canvas_agents/:appGroupId",
    params: { appGroupId },
  }),
  [INTERACTION_SITES.CATALOG]: (appGroupId) => ({
    pathname: "/agent_console/data_agents/:appGroupId",
    params: { appGroupId },
  }),
  [INTERACTION_SITES.CAMPAIGN_SETUP]: (appGroupId) => ({
    pathname: "/agent_console/qa_agents/:appGroupId",
    params: { appGroupId },
  }),
  [INTERACTION_SITES.CANVAS_QA]: (appGroupId) => ({
    pathname: "/agent_console/qa_agents/:appGroupId",
    params: { appGroupId },
  }),
};

/** Returns the route to the digest scoped to the agent's interaction site. */
export const agentDigestRouteGetter = (site: InteractionSite, appGroupId: string): AgentDigestRoute => {
  return agentDigestRouteBuilders[site](appGroupId);
};
